// Command palette. Filters across topic names + a fixed list of commands.

import { useEffect, useMemo, useRef, useState } from "react";
import { tStyles } from "../styles";

export type CommandAction =
  | { kind: "topic"; name: string; label: string; hint: string }
  | { kind: "cmd"; label: string; hint: string; run: () => void };

interface Props {
  topics: Array<{ name: string; hint: string }>;
  commands: Array<{ label: string; run: () => void }>;
  onSelectTopic: (name: string) => void;
  onClose: () => void;
}

export function CommandBar({ topics, commands, onSelectTopic, onClose }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState("");
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const all: CommandAction[] = useMemo(() => {
    return [
      ...commands.map((c) => ({ kind: "cmd" as const, label: c.label, hint: "cmd", run: c.run })),
      ...topics.map((t) => ({
        kind: "topic" as const,
        name: t.name,
        label: t.name,
        hint: t.hint,
      })),
    ];
  }, [topics, commands]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? all
        .filter(
          (a) =>
            a.label.toLowerCase().includes(q) || a.hint.toLowerCase().includes(q),
        )
        .slice(0, 12)
    : all.filter((a) => a.kind === "topic").slice(0, 10);

  const go = (i: number) => {
    const a = filtered[i];
    if (!a) return;
    if (a.kind === "cmd") {
      a.run();
      onClose();
    } else {
      onSelectTopic(a.name);
      onClose();
    }
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(idx);
    }
  };

  return (
    <div style={tStyles.cmdBackdrop} onClick={onClose}>
      <div style={tStyles.cmdPanel} onClick={(e) => e.stopPropagation()}>
        <div style={tStyles.cmdInputRow}>
          <span style={tStyles.cmdPrompt}>&gt;</span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIdx(0);
            }}
            onKeyDown={onKey}
            placeholder="search companies or run a command (try: window 7d, view table)"
            style={tStyles.cmdInput}
          />
          <span style={tStyles.cmdHint}>ESC to close</span>
        </div>
        <div style={tStyles.cmdResults}>
          {filtered.length === 0 && <div style={tStyles.cmdEmpty}>no matches</div>}
          {filtered.map((a, i) => (
            <button
              key={a.label + i}
              onClick={() => go(i)}
              onMouseEnter={() => setIdx(i)}
              style={{
                ...tStyles.cmdRow,
                ...(i === idx ? tStyles.cmdRowActive : {}),
              }}
            >
              <span style={tStyles.cmdRowKind}>{a.kind === "cmd" ? "◆" : "·"}</span>
              <span style={tStyles.cmdRowLabel}>{a.label}</span>
              <span style={tStyles.cmdRowHint}>{a.hint}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
